import { useCallback } from 'react';

import { useHistory } from 'react-router-dom';

import type { List } from 'immutable';

import type { History } from 'history';

import { useAppSelector } from 'mastodon/store';

import type { Mention } from './embedded_status';

const normalizeHandle = (value?: string) =>
  value?.trim().replace(/^@/, '').toLowerCase();

const handleMentionClick = (
  history: History,
  mention: Mention,
  e: MouseEvent,
) => {
  if (e.button === 0 && !(e.ctrlKey || e.metaKey)) {
    e.preventDefault();
    history.push(`/@${mention.get('acct')}`);
  }
};

const handleHashtagClick = (
  history: History,
  hashtag: string,
  e: MouseEvent,
) => {
  if (e.button === 0 && !(e.ctrlKey || e.metaKey)) {
    e.preventDefault();
    history.push(`/tags/${hashtag.replace(/^#/, '')}`);
  }
};

export const EmbeddedStatusContent: React.FC<{
  content: string;
  mentions: List<Mention>;
  language: string;
  className?: string;
}> = ({ content, mentions, language, className }) => {
  const history = useHistory();
  const accounts = useAppSelector((state) => state.accounts);

  const findMentionForLink = useCallback(
    (link: HTMLAnchorElement) => {
      const linkUrl = new URL(link.href, window.location.origin);
      const linkPath = normalizeHandle(
        decodeURIComponent(linkUrl.pathname).replace(/^\/@/, ''),
      );
      const linkText = normalizeHandle(link.textContent ?? undefined);

      return mentions.find((item) => {
        const mentionUrl = new URL(item.get('url'), window.location.origin);
        const mentionAcct = normalizeHandle(item.get('acct'));
        const mentionUsername = normalizeHandle(item.get('username'));
        const mentionAcctUsername = mentionAcct?.split('@')[0];

        return (
          linkUrl.href === mentionUrl.href ||
          linkUrl.pathname === mentionUrl.pathname ||
          linkPath === mentionAcct ||
          linkPath === mentionUsername ||
          linkPath === mentionAcctUsername ||
          linkText === mentionAcct ||
          linkText === mentionUsername ||
          linkText === mentionAcctUsername ||
          linkText === item.get('id')
        );
      });
    },
    [mentions],
  );

  const handleContentRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) {
        return;
      }

      const links = node.querySelectorAll<HTMLAnchorElement>('a');

      for (const link of links) {
        link.classList.add('status-link');

        const mention = findMentionForLink(link);

        if (mention) {
          if (link.dataset.mentionAccountId !== mention.get('id')) {
            link.addEventListener(
              'click',
              handleMentionClick.bind(null, history, mention),
              false,
            );
          }

          link.setAttribute('title', `@${mention.get('acct')}`);
          link.setAttribute('href', `/@${mention.get('acct')}`);
          link.dataset.mentionAccountId = mention.get('id');

          const account = accounts.get(mention.get('id'));
          const displayName =
            account?.display_name ||
            mention.get('display_name') ||
            mention.get('username') ||
            mention.get('acct');

          if (displayName) {
            link.textContent = displayName;
          }

        } else if (
          link.textContent?.[0] === '#' ||
          link.previousSibling?.textContent?.endsWith('#')
        ) {
          link.addEventListener(
            'click',
            handleHashtagClick.bind(null, history, link.text),
            false,
          );
          link.setAttribute('href', `/tags/${link.text.replace(/^#/, '')}`);
        } else {
          link.setAttribute('title', link.href);
          link.classList.add('unhandled-link');
        }
      }
    },
    [accounts, findMentionForLink, history],
  );

  return (
    <div
      className={className}
      ref={handleContentRef}
      lang={language}
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
};
